/*
 * IngestProgressCard — the professional loading state shown while Orbit reads
 * a shared URL or an uploaded file/document.
 *
 * The ingest endpoints (/api/intake/ingest, /api/intake/ingest-file) are a
 * single blocking request — there is no streamed progress to subscribe to.
 * So this card drives a HONEST staged progression on the client: it walks the
 * real stages of the read (fetch/upload → read → extract → match), advancing
 * on a timer and HOLDING at the final stage until the request resolves and the
 * parent unmounts this card. It never claims "done" — completion is the card
 * disappearing and the result turn taking its place.
 *
 * Brand-true (Agents Anywhere): IBM Plex, #18191d blue, soft surface, mono
 * uppercase eyebrow, sentence-case stage labels, no emoji, lucide glyphs.
 */

import React, { useEffect, useState } from 'react';
import { FileText, Globe, Check } from 'lucide-react';
import '../styles/ingest-progress.css';

const STAGES: Record<'doc' | 'url', string[]> = {
  doc: [
    'Uploading the file',
    'Reading the document',
    'Pulling out what describes your business',
    'Matching it to your dossier',
  ],
  url: [
    'Fetching the page',
    'Reading the content',
    "Understanding what's relevant to your business",
    'Matching it to your dossier',
  ],
};

export interface IngestProgressCardProps {
  /** The site host or the file name being read. */
  host: string;
  /** 'doc' for uploaded files, 'url' for shared links. */
  kind?: 'url' | 'doc';
}

export function IngestProgressCard({ host, kind = 'url' }: IngestProgressCardProps) {
  const stages = STAGES[kind === 'doc' ? 'doc' : 'url'];
  const [active, setActive] = useState(0);

  useEffect(() => {
    // Advance until the second-to-last stage quickly, then settle on the final
    // stage and wait there — the card unmounts when the read returns. The first
    // hop is fast (it feels responsive); the rest pace out so the labels are
    // readable rather than flickering.
    if (active >= stages.length - 1) return;
    const delay = active === 0 ? 650 : 1500;
    const t = window.setTimeout(
      () => setActive((a) => Math.min(a + 1, stages.length - 1)),
      delay,
    );
    return () => window.clearTimeout(t);
  }, [active, stages.length]);

  const Icon = kind === 'doc' ? FileText : Globe;
  const eyebrow = kind === 'doc' ? 'Reading document' : 'Reading site';

  return (
    <div className="aa-ingest" role="status" aria-live="polite">
      <div className="aa-ingest-head">
        <div className="aa-ingest-icon">
          <Icon size={18} strokeWidth={2} />
        </div>
        <div className="aa-ingest-headtext">
          <span className="aa-ingest-eyebrow">{eyebrow}</span>
          <span className="aa-ingest-host" title={host}>{host}</span>
        </div>
      </div>

      <div className="aa-ingest-bar" aria-hidden="true">
        <span className="aa-ingest-bar-fill" />
      </div>

      <ol className="aa-ingest-steps">
        {stages.map((label, i) => {
          const state = i < active ? 'done' : i === active ? 'active' : 'pending';
          return (
            <li key={label} className={`aa-ingest-step aa-ingest-step--${state}`}>
              <span className="aa-ingest-step-marker">
                {state === 'done'
                  ? <Check size={11} strokeWidth={3} />
                  : <span className="aa-ingest-step-dot" />}
              </span>
              <span className="aa-ingest-step-label">{label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default IngestProgressCard;
